import { FeedbackSubmitModel } from '@/models/FeedbackSubmitModel'
import axios, { AxiosTransformer, AxiosInstance } from 'axios'
import { FeedbackRequestCreateModel } from '@/models/FeedbackRequestCreateModel'
import { FeedbackTemplateModel } from '@/models/FeedbackTemplateModel'
import { FeedbackRequestSharedModel } from '@/models/FeedbackRequestSharedModel'
import UnauthorizedError from '@/errors/UnauthorizedError'

export class FeedbackApi {
  public constructor (baseUrl: string) {
    this.baseUrl = baseUrl
    this.client = axios.create({
      baseURL: baseUrl
    })
  }
  baseUrl: string
  client: AxiosInstance

  public async getFeedbackTemplateBySharedId (sharedId?: string) : Promise<FeedbackRequestSharedModel> {
    return this.client.get(this.baseUrl + `/feedbackrequest/for/${sharedId}?includeTemplate=true`)
      .then(
        r => r.data,
        r => new Error(`Unable to get questions for response id '${sharedId}'`))
  }

  public async getFeedbackTemplates (category?: string) : Promise<FeedbackTemplateModel[]> {
    return this.client.get(this.baseUrl + `/template/search`)
      .then(
        r => r.data,
        r => this.errorHandler(r))
  }

  private errorHandler (r: any) {
    let error = new Error('Unable to get templates')
    if (r.status === 401) {
      error = new UnauthorizedError(`Unable to get templates`)
    }
    return Promise.reject(error)
  }

  public async postFeedback (feedbackModel: FeedbackSubmitModel) {
    return this.client.post(this.baseUrl + `/feedbackrequest/for/${feedbackModel.sharedId}`, feedbackModel)
      .then(
        r => r.data,
        r => new Error('Unable to submit feedback.'))
  }

  public async postFeedbackRequest (feedbackRequestModel: FeedbackRequestCreateModel) {
    return this.client.post(this.baseUrl + '/feedbackrequest', feedbackRequestModel)
      .then(
        r => r.data,
        r => new Error('Unable to create feedback request'))
  }

  public async getFeedbackRequestById (feedbackRequestId: string) {
    return this.client.get(`/feedbackrequest/${feedbackRequestId}`)
      .then(
        r => (r ? r.data : null),
        r => new Error('Unable to get this feedback request')
      )
  }

  public async searchFeedbackRequests (pageNumber: number, pageSize: number) {
    return this.client.get(`/feedbackrequest/search`, {
      params: {
        pageNumber: pageNumber,
        pageSize: pageSize
      }
    })
      .then(
        r => (r ? r.data : null),
        r => this.errorHandler(r)
      )
  }

  public async getFeedbackRequestAnalysis (feedbackRequestId: string) {
    return this.client.get(`/feedbackanalysis/${feedbackRequestId}`)
      .then(
        r => (r ? r.data : null),
        r => new Error('Unable to get analysis for this feedback request')
      )
  }

  public async syncMyUser () {
    return this.client.post(`/users/me`)
      .then(
        r => r.data,
        r => Promise.reject(new Error('Unable to sync my user'))
      )
  }

  public async getParaphrase (text?: String) : Promise<any> {
    return this.client.post(this.baseUrl + `/paraphrase`, { text: text || '' })
      .then(
        r => r.data, r => new Error('Unable to paraphrase'))
  }

  public async getGuid () : Promise<string> {
    return this.client.get(this.baseUrl + '/guid')
      .then(r => r.data, r => new Error('Unable to create unique id'))
  }

  private jsonSerializer (key: String, value: any): any {
    if (key.startsWith('_')) {
      return undefined
    } else {
      return value
    }
  }
}
